import React, { useState, useRef, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import styles from "./Editbusinessdetails.module.css";
import EditHeader from "../EditHeader/EditHeader";
import { validateField } from "../../utils/validation";
import { isPythonBackend, authHeaders } from "../../utils/apiBackend";
import {
  validateWebsite,
  listSiteMap,
  getUserAgentMergedDataForAgentUpdate,
} from "../../Store/apiStore";
import filterCompanyPages from "../../utils/companyKeyboards";
import CustomAutoSearch from "../CustomAutocomplete/CustomAutoSearch";
import GoogleAddressInputWithDropdown from "../GoogleAddressInputWithDropdown/GoogleAddressInputWithDropdown";
import { businessTypes } from "../../lib/businessCategories";
import { businessServices } from "../../lib/businessServices";
import Loader2 from "../Loader2/Loader2";
import PopUp from "../Popup/Popup";
import axios from "axios";

const EditBusinessDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [agentName, setAgentName] = useState(
    sessionStorage.getItem("agentName") || ""
  );
  const { agentId, businessId } = location.state || {};

  // Get IDs from session storage as fallback
  const editAgentId = sessionStorage.getItem("SelectAgentId") || agentId;
  const editBusinessId =
    sessionStorage.getItem("SelectAgentBusinessId") || businessId;

  // Loading states
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isFetchingPlaceDetails, setIsFetchingPlaceDetails] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    businessType: "",
    businessSubtype: "",
    customBusiness: "",
    displayBusinessName: "",
    googleListing: "",
    noGoogleListing: false,
    businessUrl: "",
    noBusinessWebsite: false,
    placeDetails: null,
    sitemapUrls: [],
    selectedSitemapUrls: [],
    hours: "",
    selectedServices: [],
    customServices: [],
  });

  const DEFAULT_HOURS_FALLBACK = [
    "Monday: 9:00 AM – 6:00 PM",
    "Tuesday: 9:00 AM – 6:00 PM",
    "Wednesday: 9:00 AM – 6:00 PM",
    "Thursday: 9:00 AM – 6:00 PM",
    "Friday: 9:00 AM – 6:00 PM",
    "Saturday: 9:00 AM – 6:00 PM",
    "Sunday: Closed",
  ];

  const getDefaultHoursString = () => DEFAULT_HOURS_FALLBACK.join(" | ");

  const hasValidGmbHours = (hours) => {
    if (!hours) return false;
    if (Array.isArray(hours)) return hours.filter(Boolean).length > 0;
    if (typeof hours === "string") return hours.trim().length > 0;
    return false;
  };

  const [originalData, setOriginalData] = useState(null);

  // UI states
  const [showBusinessTypeDropdown, setShowBusinessTypeDropdown] =
    useState(false);
  const [businessTypeSearch, setBusinessTypeSearch] = useState("");
  const [googleResults, setGoogleResults] = useState([]);
  const [isUrlValid, setIsUrlValid] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("us");
  const [filteredPages, setFilteredPages] = useState([]);
  const [selectedSitemapCount, setSelectedSitemapCount] = useState(0);

  // Service UI states
  const [serviceSearchTerm, setServiceSearchTerm] = useState("");
  const [showAddCustomService, setShowAddCustomService] = useState(false);
  const [customServiceInput, setCustomServiceInput] = useState("");

  // Error and validation states
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Popup state
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState("success");
  const [popupMessage, setPopupMessage] = useState("");

  // Refs
  const typingTimeout = useRef(null);
  const addressInputRef = useRef(null);
  const servicesListRef = useRef(null);
  const hasScrolledToSelectedService = useRef(false);

  /* ==========================================
     UTILITY FUNCTIONS
  ========================================== */
  const safeParse = (value, fallback = null) => {
    try {
      if (typeof value === "string") {
        const cleaned = value.trim();
        if (
          (cleaned.startsWith("[") && cleaned.endsWith("]")) ||
          (cleaned.startsWith("{") && cleaned.endsWith("}")) ||
          (cleaned.startsWith('"') && cleaned.endsWith('"'))
        ) {
          return JSON.parse(cleaned);
        }
      }
      return value;
    } catch {
      return fallback;
    }
  };

  const normalizeWebsite = (raw) => {
    if (!raw) return "";
    try {
      let v = String(raw).trim();
      v = v.replace(/^(https?:\/\/)+(www\.)?/gi, "");
      v = v.split("/")[0].toLowerCase().replace(/\s+/g, "");
      if (!v) return "";
      const domainRegex = /^[\w-]+(\.[\w-]{2,})+$/i;
      if (!domainRegex.test(v)) return "";
      return "https://" + v;
    } catch {
      return "";
    }
  };

  const isUrlEffectivelyEmpty = (val = "") => {
    const v = val.trim().toLowerCase();
    return v === "" || v === "https://" || v === "http://";
  };

  const extractAddressComponents = (addressComponents) => {
    if (!Array.isArray(addressComponents)) return {};

    const getComponent = (
      primaryType,
      fallbackType = null,
      useShort = false
    ) => {
      const comp = addressComponents.find(
        (c) =>
          c.types.includes(primaryType) ||
          (fallbackType && c.types.includes(fallbackType))
      );
      return comp ? (useShort ? comp.short_name : comp.long_name) : "";
    };

    return {
      city: getComponent("locality", "sublocality_level_1"),
      state: getComponent("administrative_area_level_1"),
      state_code: getComponent("administrative_area_level_1", null, true),
      country: getComponent("country"),
      country_code: getComponent("country", null, true),
      postal_code: getComponent("postal_code"),
      street_number: getComponent("street_number"),
      route: getComponent("route"),
    };
  };

  /* ==========================================
     BUILD SEARCHABLE BUSINESS TYPE LIST
  ========================================== */
  const buildSearchableList = () => {
    const items = [];
    const seen = new Set();

    businessTypes.forEach((bt) => {
      const mainType = bt.type;

      if (bt.subtypes && bt.subtypes.length > 0) {
        bt.subtypes.forEach((sub) => {
          const key = sub.toLowerCase();
          if (!seen.has(key)) {
            seen.add(key);
            items.push({
              display: sub,
              type: mainType,
              subtype: sub,
              isMainType: false,
            });
          }
        });
      }

      const mainTypeKey = mainType.toLowerCase();
      if (!seen.has(mainTypeKey)) {
        seen.add(mainTypeKey);
        items.push({
          display: mainType,
          type: mainType,
          subtype: null,
          isMainType: true,
        });
      }
    });

    return items;
  };

  const searchableList = buildSearchableList();

  const filteredBusinessTypes = searchableList.filter((item) =>
    item.display.toLowerCase().includes(businessTypeSearch.toLowerCase())
  );

  const isNewCategory =
    businessTypeSearch.trim() !== "" && filteredBusinessTypes.length === 0;

  /* ==========================================
     FETCH DATA FROM API
  ========================================== */
  const fetchBusinessData = async () => {
    if (!editAgentId || !editBusinessId) {
      console.error("Missing agentId or businessId");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const response = await getUserAgentMergedDataForAgentUpdate(
        editAgentId,
        editBusinessId
      );

      const agent = response?.data?.agent;
      const business = response?.data?.business;

      if (!business) {
        throw new Error("No business data found");
      }

      const kb = safeParse(business?.knowledge_base_texts, {});
      const businessAddrRaw = safeParse(business?.businessAddress, {});
      const addrDetails = businessAddrRaw?.addressDetails || {};

      const scraped = safeParse(business.scrapedUrls, []);

      const rawHours = kb?.hours || "";
      const loadedHours = hasValidGmbHours(rawHours)
        ? rawHours
        : getDefaultHoursString();

      const placeDetails = {
        businessName: business.businessName || "",
        address:
          kb?.address ||
          kb?.formattedAddress ||
          kb?.streetAddress ||
          addrDetails?.address ||
          business?.address1 ||
          "",
        phone: kb?.phone || addrDetails?.phone || business?.phone || "",
        internationalPhone: kb?.phone || addrDetails?.phone || business?.phone || "",
        email: kb?.email || addrDetails?.email || business?.buisnessEmail || "",
        city: kb?.city || addrDetails?.city || business?.city || "",
        state: kb?.state || addrDetails?.state || business?.state || "",
        state_code:
          kb?.state_code ||
          addrDetails?.state_code ||
          business?.state_code ||
          "",
        country: kb?.country || addrDetails?.country || business?.country || "",
        country_code:
          kb?.country_code ||
          addrDetails?.country_code ||
          business?.country_code ||
          "",
        postal_code:
          kb?.postalCode ||
          addrDetails?.postalCode ||
          addrDetails?.postal_code ||
          business?.postal_code ||
          business?.zip ||
          "",
        street_number: kb?.street_number || addrDetails?.street_number || "",
        route: kb?.route || addrDetails?.route || "",
        hours: loadedHours,
      };

      // ── Parse Services & Custom Services ──
      let loadedServices = [];
      const rawBuisnessService =
        business?.buisnessService ||
        business?.service ||
        business?.services ||
        [];
      if (Array.isArray(rawBuisnessService)) {
        loadedServices = rawBuisnessService;
      } else if (typeof rawBuisnessService === "string") {
        try {
          const parsed = JSON.parse(rawBuisnessService);
          loadedServices = Array.isArray(parsed) ? parsed : [parsed].filter(Boolean);
        } catch {
          loadedServices = rawBuisnessService.trim() ? [rawBuisnessService.trim()] : [];
        }
      }

      let loadedCustom = [];
      const rawCustomServices = business?.customServices;
      if (Array.isArray(rawCustomServices)) {
        loadedCustom = rawCustomServices;
      } else if (typeof rawCustomServices === "string") {
        try {
          const parsed = JSON.parse(rawCustomServices);
          loadedCustom = Array.isArray(parsed) ? parsed : [parsed].filter(Boolean);
        } catch {
          loadedCustom = [];
        }
      }

      const flatCustom = loadedCustom
        .map((item) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object") {
            if (item.service) return item.service;
            if (Array.isArray(item.services)) return item.services;
          }
          return item;
        })
        .flat()
        .filter(Boolean);

      /*
       * Tick sirf `service` (buisnessService) se aayega. Pehle yahan
       * loadedServices + flatCustom ka union hota tha, jiska matlab tha ki
       * customServices me pada har naam apne aap checked ho jaata — chahe user
       * ne use uncheck hi kyun na kiya ho. flatCustom ab bhi rakha jaata hai
       * taaki wo naam list me dikhe (allAvailableServices), par checked hone ka
       * faisla ek hi jagah se hota hai.
       */
      const allInitialServices = Array.from(new Set(loadedServices)).filter(
        Boolean
      );

      const loadedData = {
        businessType: business.businessType || "",
        businessSubtype: business.subType || "",
        customBusiness: business.customBuisness || "",
        displayBusinessName:
          business.googleBusinessName || business.businessName || "",
        googleListing: business.googleUrl || "",
        noGoogleListing: !business.isGoogleListing,
        businessUrl: business.webUrl || "",
        noBusinessWebsite: !business.webUrl,
        hours: loadedHours,
        placeDetails: placeDetails,
        selectedSitemapUrls: scraped,
        selectedServices: allInitialServices,
        customServices: flatCustom,
      };

      setAgentName(agent?.agentName || "Agent");
      setFormData(loadedData);
      setOriginalData(JSON.parse(JSON.stringify(loadedData)));

      setBusinessTypeSearch(business.subType || business.businessType || "");

      if (kb?.country_code || business?.country_code) {
        setSelectedCountry((kb?.country_code || business?.country_code).toLowerCase());
      }

      if (business.webUrl && !isUrlEffectivelyEmpty(business.webUrl)) {
        setIsUrlValid(true);
        setFilteredPages(scraped);
        const selectedCount = scraped?.length;
        setSelectedSitemapCount(selectedCount);
      }
    } catch (error) {
      console.error("Failed to fetch business data:", error);
      setShowPopup(true);
      setPopupType("failed");
      setPopupMessage("Failed to load business data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinessData();
  }, [editAgentId, editBusinessId]);

  /* ==========================================
     SERVICES COMPUTED LIST
  ========================================== */
  const matchedCategory = useMemo(() => {
    const currentType = (formData.businessType || "").toLowerCase();
    const currentSubType = (formData.businessSubtype || "").toLowerCase();

    return (
      businessServices?.find(
        (b) =>
          (currentType &&
            ((b.type || "").toLowerCase() === currentType ||
              (b.subtype || "").toLowerCase() === currentType)) ||
          (currentSubType &&
            ((b.type || "").toLowerCase() === currentSubType ||
              (b.subtype || "").toLowerCase() === currentSubType))
      ) ||
      (currentType === "other"
        ? businessServices?.find((b) => b.type === "Other Local Business")
        : null)
    );
  }, [formData.businessType, formData.businessSubtype]);

  /*
   * Custom category = user ne apni category khud type karke "Add" ki hai.
   * Uski koi default services nahi hoti, isliye "Add More Services" apne aap
   * ticked rehta hai, search bar nahi dikhta, aur services ki list tabhi
   * dikhti hai jab user kam se kam ek service add kar le.
   * `customBusiness` save ke baad backend se wapas na bhi aaye, to bhi
   * matchedCategory ka na milna yahi batata hai.
   */
  const isCustomCategory =
    !!formData.customBusiness || (!!formData.businessType && !matchedCategory);

  useEffect(() => {
    setShowAddCustomService(isCustomCategory);
    // Custom category me search bar chhupa hota hai — purana search term reh
    // gaya to list bina kisi dikhne wali wajah ke filter hoti rahegi.
    if (isCustomCategory) setServiceSearchTerm("");
  }, [isCustomCategory]);

  const allAvailableServices = useMemo(() => {
    const defaultCategoryServices = (matchedCategory?.services || []).filter(
      (s) => s !== "Other"
    );

    const combined = [
      ...defaultCategoryServices,
      ...(formData.selectedServices || []),
      ...(formData.customServices || []),
    ].filter(Boolean);

    return Array.from(new Set(combined));
  }, [matchedCategory, formData.selectedServices, formData.customServices]);

  const filteredServices = useMemo(() => {
    const term = serviceSearchTerm.trim().toLowerCase();
    if (!term) return allAvailableServices;
    return allAvailableServices.filter(
      (s) => typeof s === "string" && s.toLowerCase().includes(term)
    );
  }, [allAvailableServices, serviceSearchTerm]);

  const categoryIcon = matchedCategory?.icon || "/images/other.png";
  const categorySubtype =
    matchedCategory?.subtype || formData.businessSubtype || "Business Service";

  // Auto-scroll services list to the first selected/ticked service on load
  useEffect(() => {
    if (
      !loading &&
      formData.selectedServices?.length > 0 &&
      !hasScrolledToSelectedService.current
    ) {
      const timer = setTimeout(() => {
        const container = servicesListRef.current;
        if (container) {
          const selectedEl = container.querySelector(
            `.${styles.serviceOptionSelected}`
          );
          if (selectedEl) {
            const containerTop = container.getBoundingClientRect().top;
            const elTop = selectedEl.getBoundingClientRect().top;
            const relativeTop = elTop - containerTop;
            container.scrollTo({
              top: container.scrollTop + relativeTop - 8,
              behavior: "smooth",
            });
            hasScrolledToSelectedService.current = true;
          }
        }
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [loading, formData.selectedServices, filteredServices]);

  const handleServiceToggle = (serviceName) => {
    setFormData((prev) => {
      const current = prev.selectedServices || [];
      const isRemoving = current.includes(serviceName);
      const updated = isRemoving
        ? current.filter((s) => s !== serviceName)
        : [...current, serviceName];

      /*
       * Uncheck karne par custom service ko customServices se NAHI nikalte —
       * warna wo list se hi gayab ho jaati aur user use dobara tick nahi kar
       * paata. Wo unchecked row ban kar list me rehti hai; save ke waqt
       * pruneCustomServices unticked custom services ko hata deta hai, isliye
       * API ko sirf ticked wali hi jaati hain.
       */
      return {
        ...prev,
        selectedServices: updated,
      };
    });

    setErrors((prev) => ({ ...prev, selectedServices: null }));
  };

  const handleAddCustomService = () => {
    const trimmed = customServiceInput.trim();
    if (!trimmed) return;

    setFormData((prev) => {
      const prevCustom = prev.customServices || [];
      const prevSelected = prev.selectedServices || [];

      const updatedCustom = prevCustom.includes(trimmed)
        ? prevCustom
        : [...prevCustom, trimmed];

      const updatedSelected = prevSelected.includes(trimmed)
        ? prevSelected
        : [...prevSelected, trimmed];

      return {
        ...prev,
        customServices: updatedCustom,
        selectedServices: updatedSelected,
      };
    });

    setCustomServiceInput("");
    setErrors((prev) => ({ ...prev, selectedServices: null }));

    /*
     * Sirf services wali list neeche jaani chahiye. scrollIntoView apne saare
     * ancestors ko scroll karta hai, isliye pura page khisak jaata tha —
     * container par seedha scrollTo karne se sirf list chalti hai.
     */
    setTimeout(() => {
      const container = servicesListRef.current;
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
      }
    }, 100);
  };

  /* ==========================================
     GOOGLE PLACES API
  ========================================== */
  useEffect(() => {
    const interval = setInterval(() => {
      if (window.google?.maps?.places) {
        clearInterval(interval);
      }
    }, 300);
    return () => clearInterval(interval);
  }, []);

  const fetchGoogleResults = (text) => {
    if (!window.google?.maps?.places || !text) {
      setGoogleResults([]);
      return;
    }

    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      { input: text, types: ["establishment"] },
      (predictions) => {
        setGoogleResults(predictions || []);
      }
    );
  };

  const fetchPlaceDetails = async (placeId) => {
    if (!window.google?.maps?.places) return;

    setIsFetchingPlaceDetails(true);

    const service = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );

    const getPlaceDetailsPromise = () => {
      return new Promise((resolve, reject) => {
        service.getDetails(
          {
            placeId,
            fields: [
              "place_id",
              "name",
              "url",
              "website",
              "formatted_address",
              "international_phone_number",
              "formatted_phone_number",
              "opening_hours",
              "rating",
              "user_ratings_total",
              "business_status",
              "types",
              "address_components",
            ],
          },
          (result, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              resolve(result);
            } else {
              reject(new Error(`Place details fetch failed: ${status}`));
            }
          }
        );
      });
    };

    try {
      const result = await getPlaceDetailsPromise();

      const addressFields = extractAddressComponents(
        result.address_components || []
      );
      const rawHours = result.opening_hours?.weekday_text || [];
      const finalHours = hasValidGmbHours(rawHours)
        ? rawHours.join(" | ")
        : getDefaultHoursString();

      const businessData = {
        businessName: result.name || "",
        address: result.formatted_address || "",
        phone:
          result.international_phone_number ||
          result.formatted_phone_number ||
          "",
        internationalPhone: result.international_phone_number || "",
        email: "",
        hours: finalHours,
        ...addressFields,
      };

      const normalizedWebsite = normalizeWebsite(result.website);

      setFormData((prev) => ({
        ...prev,
        placeDetails: businessData,
        businessUrl: normalizedWebsite || "",
        noBusinessWebsite: !normalizedWebsite,
      }));

      if (addressFields.country_code) {
        setSelectedCountry(addressFields.country_code.toLowerCase());
      }

      if (normalizedWebsite) {
        await handleUrlVerification(normalizedWebsite);
      } else {
        setIsUrlValid(null);
        setFilteredPages([]);
        setFormData((prev) => ({
          ...prev,
          sitemapUrls: [],
          selectedSitemapUrls: [],
        }));
        setSelectedSitemapCount(0);
      }
    } catch (error) {
      console.error("Failed to fetch place details:", error);
      setShowPopup(true);
      setPopupType("failed");
      setPopupMessage("Failed to fetch business details from Google");
    } finally {
      setIsFetchingPlaceDetails(false);
    }
  };

  const handlePlaceSelect = async (item) => {
    const name =
      item?.structured_formatting?.main_text || item?.description || "";
    const googleLink = `https://www.google.com/search?q=${encodeURIComponent(
      item.description
    )}`;

    setFormData((prev) => ({
      ...prev,
      displayBusinessName: name,
      googleListing: googleLink,
      noGoogleListing: false,
    }));

    setGoogleResults([]);
    await fetchPlaceDetails(item.place_id);
  };

  /* ==========================================
     HANDLE ADDRESS CHANGE
  ========================================== */
  const handleAddressChange = async (selectedAddress, placeId = null) => {
    if (placeId && window.google?.maps?.places) {
      const geocoder = new window.google.maps.Geocoder();

      try {
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ placeId: placeId }, (results, status) => {
            if (status === "OK" && results[0]) {
              resolve(results[0]);
            } else {
              reject(new Error("Geocoding failed"));
            }
          });
        });

        const addressFields = extractAddressComponents(
          result.address_components
        );

        setFormData((prev) => ({
          ...prev,
          placeDetails: {
            ...prev.placeDetails,
            address: result.formatted_address || selectedAddress,
            address_components: result.address_components,
            ...addressFields,
          },
        }));

        if (addressFields.country_code) {
          setSelectedCountry(addressFields.country_code.toLowerCase());
        }
      } catch (error) {
        console.error("Failed to extract address components:", error);
        setFormData((prev) => ({
          ...prev,
          placeDetails: {
            ...prev.placeDetails,
            address: selectedAddress,
          },
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        placeDetails: {
          ...prev.placeDetails,
          address: selectedAddress,
        },
      }));
    }
  };

  /* ==========================================
     URL VERIFICATION
  ========================================== */
  const handleUrlVerification = async (url) => {
    if (!url || isUrlEffectivelyEmpty(url)) return false;

    setIsVerifying(true);
    setIsUrlValid(null);

    try {
      const result = await validateWebsite(url);

      if (!result?.valid) {
        setIsUrlValid(false);
        setErrors((prev) => ({ ...prev, businessUrl: "Invalid URL" }));
        setIsVerifying(false);
        return false;
      }

      const finalUrl = (result?.finalUrl || url || "").trim();
      setIsUrlValid(true);
      setErrors((prev) => ({ ...prev, businessUrl: null }));

      setFormData((prev) => ({ ...prev, businessUrl: finalUrl }));

      const res = await listSiteMap(finalUrl);
      let urls = res?.urls || [];
      if (finalUrl && !urls.includes(finalUrl)) urls.unshift(finalUrl);

      const pages = await filterCompanyPages(urls);
      const fp = Array.isArray(pages) ? pages : urls;

      const first10 = fp.slice(0, 10);
      setFilteredPages(first10);

      const defaultSelected = first10.map((u) => ({
        url: u,
        checkedStatus: true,
      }));

      setFormData((prev) => ({
        ...prev,
        sitemapUrls: first10,
        selectedSitemapUrls: defaultSelected,
      }));

      setSelectedSitemapCount(10);
      setIsVerifying(false);
      return true;
    } catch (error) {
      console.error("URL verification failed:", error);
      setIsUrlValid(false);
      setErrors((prev) => ({ ...prev, businessUrl: "Verification failed" }));
      setIsVerifying(false);
      return false;
    }
  };

  const handleUrlChange = (value) => {
    let input = value || "";

    if (input.trim() === "" || input === "http://" || input === "https://") {
      setFormData((prev) => ({ ...prev, businessUrl: "" }));
      setIsUrlValid(null);
      setFilteredPages([]);
      if (typingTimeout.current) clearTimeout(typingTimeout.current);
      return;
    }

    let cleanDomain = input.replace(/^(https?:\/\/)+(www\.)?/gi, "");

    if (!cleanDomain) {
      setFormData((prev) => ({ ...prev, businessUrl: "" }));
      return;
    }

    const finalUrl = `https://${cleanDomain}`;
    setFormData((prev) => ({
      ...prev,
      businessUrl: finalUrl,
      noBusinessWebsite: false,
    }));

    if (!cleanDomain.includes(".")) {
      setIsUrlValid(null);
      setFilteredPages([]);
      return;
    }

    if (touched.businessUrl) {
      const err = validateField("businessUrl", finalUrl);
      setErrors((prev) => ({ ...prev, businessUrl: err }));
    }

    if (typingTimeout.current) clearTimeout(typingTimeout.current);

    typingTimeout.current = setTimeout(() => {
      if (cleanDomain.length >= 3 && cleanDomain.includes(".")) {
        handleUrlVerification(finalUrl);
      }
    }, 800);
  };

  /* ==========================================
     HANDLE FORM CHANGES
  ========================================== */
  const handleBusinessTypeSelect = (item) => {
    setBusinessTypeSearch(item.display);
    setShowBusinessTypeDropdown(false);

    // Subtype ko tabhi compare karo jab wo actually ho. Warna main type chunne
    // par ("" === "") chal jaata tha aur pehli bina-subtype wali entry match ho
    // kar galat category ki services le aati thi.
    const matchedBiz = businessServices.find(
      (b) =>
        (b.type || "").toLowerCase() === (item.type || "").toLowerCase() ||
        (item.subtype &&
          (b.subtype || "").toLowerCase() === item.subtype.toLowerCase())
    );

    const autoSelectedServices = matchedBiz
      ? matchedBiz.services.filter((s) => s !== "Other").slice(0, 6)
      : [];

    setFormData((prev) => ({
      ...prev,
      businessType: item.type,
      businessSubtype: item.subtype,
      customBusiness: "",
      /*
       * Category badli to purani selection ka koi matlab nahi — nayi category ki
       * services hi chunni chahiye. Pehle `autoSelectedServices` khaali hone par
       * `prev.selectedServices` rakh liya jaata tha, jisse purani category ki
       * services (aur user ki custom service) ticked hi reh jaati thi.
       * customServices bhi purani category ka hissa tha, isliye wo bhi jaayega.
       */
      selectedServices: autoSelectedServices,
      customServices: [],
    }));

    setErrors((prev) => ({
      ...prev,
      businessType: null,
      selectedServices: null,
    }));
  };

  const handleBusinessTypeCustom = () => {
    setTouched((prev) => ({ ...prev, businessType: true }));
    const err = validateField("businessType", businessTypeSearch);
    if (err) {
      setErrors((prev) => ({ ...prev, businessType: err }));
      return;
    }

    setShowBusinessTypeDropdown(false);

    setFormData((prev) => ({
      ...prev,
      businessType: businessTypeSearch,
      businessSubtype: null,
      customBusiness: businessTypeSearch,
      /*
       * Custom category ki koi default services hoti hi nahi, isliye list khaali
       * honi chahiye — user khud "Add More Services" se apni services daalega.
       * Ye reset na karne par purani category ki services ticked hi reh jaati
       * thi aur naye business ke saath chali jaati.
       */
      selectedServices: [],
      customServices: [],
    }));

    setErrors((prev) => ({ ...prev, businessType: null }));
  };

  const handlePhoneChange = (phone, countryData) => {
    const fullPhone = phone.startsWith("+") ? phone : `+${phone}`;
    const countryCode = countryData?.countryCode || "us";

    setSelectedCountry(countryCode);

    setFormData((prev) => ({
      ...prev,
      placeDetails: {
        ...prev.placeDetails,
        phone: fullPhone,
        internationalPhone: fullPhone,
        country_code: countryCode.toUpperCase(),
      },
    }));

    if (touched.phone) {
      const err = validatePhone(fullPhone);
      setErrors((prev) => ({ ...prev, phone: err }));
    }
  };

  const handleNoWebsiteCheckbox = (checked) => {
    setFormData((prev) => ({
      ...prev,
      noBusinessWebsite: checked,
      businessUrl: checked ? "" : prev.businessUrl,
      ...(checked ? { selectedSitemapUrls: [], sitemapUrls: [] } : {}),
    }));

    if (checked) {
      setIsUrlValid(null);
      setFilteredPages([]);
      setSelectedSitemapCount(0);
      setErrors((prev) => ({ ...prev, businessUrl: null }));
    }
  };

  /* ==========================================
     VALIDATION
  ========================================== */
  const validatePhone = (phone) => {
    if (!phone) return "Phone number is required";

    const phoneObj = parsePhoneNumberFromString(phone);

    if (!phoneObj || !phoneObj.isValid()) {
      return "Please enter a valid phone number";
    }

    return null;
  };

  const validateForm = () => {
    const newErrors = {};
    const newTouched = {};

    // Business Type validation
    if (!formData.businessType) {
      newErrors.businessType = "Business type is required";
      newTouched.businessType = true;
    }

    // Website validation
    if (isVerifying) {
      newErrors.businessUrl = "Please wait... Website is still validating.";
      newTouched.businessUrl = true;
    } else if (!formData.noBusinessWebsite) {
      if (isUrlEffectivelyEmpty(formData.businessUrl)) {
        newErrors.businessUrl =
          "Enter a valid website URL or check the box below";
        newTouched.businessUrl = true;
      } else if (isUrlValid === false) {
        newErrors.businessUrl = "Invalid website URL";
        newTouched.businessUrl = true;
      }
    }

    // Phone validation
    const phoneError = validatePhone(formData.placeDetails?.phone);
    if (phoneError) {
      newErrors.phone = phoneError;
      newTouched.phone = true;
    }

    // Business name validation
    if (!formData.placeDetails?.businessName && !formData.displayBusinessName) {
      newErrors.businessName = "Business name is required";
      newTouched.businessName = true;
    }

    // Address validation
    if (!formData.placeDetails?.address) {
      newErrors.address = "Address is required";
      newTouched.address = true;
    }

    // Services validation
    if (
      !formData.selectedServices ||
      formData.selectedServices.length === 0
    ) {
      newErrors.selectedServices = "Please select at least one service";
      newTouched.selectedServices = true;
    }

    setErrors(newErrors);
    setTouched(newTouched);

    return Object.keys(newErrors).length === 0;
  };

  /* ==========================================
     CHANGE DETECTION
  ========================================== */
  // Unticked custom services sirf screen par rehti hain (dobara tick karne ke
  // liye) — save hone wale data ka hissa nahi hain.
  const pruneCustomServices = (data) => ({
    ...data,
    customServices: (data.customServices || []).filter((s) =>
      (data.selectedServices || []).includes(s)
    ),
  });

  const hasChanges = () => {
    if (!originalData) return false;
    return (
      JSON.stringify(pruneCustomServices(formData)) !==
      JSON.stringify(pruneCustomServices(originalData))
    );
  };

  /* ==========================================
     SAVE HANDLER
  ========================================== */
  const handleSave = async () => {
    const isValid = validateForm();
    if (!isValid) {
      setShowPopup(true);
      setPopupType("failed");
      setPopupMessage("Please fix all validation errors");
      return;
    }

    if (!hasChanges()) {
      setShowPopup(true);
      setPopupType("failed");
      setPopupMessage("No changes detected");
      return;
    }

    try {
      setIsSaving(true);

      const addressDetails = {
        businessName: formData.placeDetails?.businessName || "",
        phone: formData.placeDetails?.phone || "",
        email: formData.placeDetails?.email || "",
        hours:
          formData.placeDetails?.hours ||
          formData.hours ||
          getDefaultHoursString(),
        address: formData.placeDetails?.address || "",
        formattedAddress: formData.placeDetails?.address || "",
        city: formData.placeDetails?.city || "",
        state: formData.placeDetails?.state || "",
        state_code: formData.placeDetails?.state_code || "",
        country: formData.placeDetails?.country || "",
        country_code: formData.placeDetails?.country_code || "",
        postalCode: formData.placeDetails?.postal_code || "",
        street_number: formData.placeDetails?.street_number || "",
        route: formData.placeDetails?.route || "",
      };

      const getSelectedSitemapUrls = () => {
        if (
          !formData.selectedSitemapUrls ||
          !Array.isArray(formData.selectedSitemapUrls)
        ) {
          return [];
        }
        return formData.selectedSitemapUrls.filter(
          (item) => item.checkedStatus === true
        );
      };

      const selectedSitemapUrls = getSelectedSitemapUrls();
      const savedData = pruneCustomServices(formData);

      const payload = {
        agent_id: editAgentId,

        // Business Type
        industry: formData.businessType,
        isBusinessSubType: formData.businessSubtype || formData.businessType,

        // Business Info
        businessName:
          formData.placeDetails?.businessName ||
          formData.displayBusinessName ||
          "",
        googleBusinessName: formData.displayBusinessName || "",
        googleListing: formData.googleListing || "",

        // Services
        service: formData.selectedServices || [],
        customServices: savedData.customServices,

        // Website
        businessWebsite: formData.noBusinessWebsite
          ? null
          : formData.businessUrl || "",
        selectedSitemapUrls: formData.noBusinessWebsite
          ? []
          : selectedSitemapUrls,
        sitemapUrls: formData.sitemapUrls || [],

        // Contact
        businessPhone: formData.placeDetails?.phone || "",
        businessEmail: formData.placeDetails?.email || "",
        country_code: formData.placeDetails?.country_code || "",

        businessAddress: JSON.stringify({
          addressDetails: addressDetails,
        }),
      };

      if (isPythonBackend()) {
        await axios.patch(
          `${process.env.REACT_APP_API_BASE_URL}/agentV2/updateAgentBusinessPython`,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );
        await fetch(
          `${process.env.REACT_APP_PY_API_BASE_URL}/agents/${editAgentId}/edit`,
          { method: "POST", headers: authHeaders() }
        );
      } else {
        await axios.patch(
          `${process.env.REACT_APP_API_BASE_URL}/agentV2/updateAgent`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      }

      setShowPopup(true);
      setPopupType("success");
      setPopupMessage("Business details updated successfully!");

      // Save ho gaya — unticked custom services ab list se bhi hat jaati hain.
      setFormData(savedData);
      setOriginalData(JSON.parse(JSON.stringify(savedData)));

      sessionStorage.setItem(
        "displayBusinessName",
        formData.displayBusinessName
      );
      sessionStorage.setItem(
        "businessDetails",
        JSON.stringify({
          businessType: formData.businessType,
          businessName: formData.placeDetails?.businessName || "",
          subType: formData.businessSubtype,
          selectedService: formData.selectedServices,
        })
      );
    } catch (error) {
      console.error("❌ Failed to save business details:", error);
      setShowPopup(true);
      setPopupType("failed");
      setPopupMessage(
        error?.response?.data?.message || "Failed to update business details"
      );
    } finally {
      setIsSaving(false);
    }
  };

  /* ==========================================
     RENDER
  ========================================== */
  if (loading) {
    return <Loader2 />;
  }

  const isSaveDisabled =
    isSaving || isVerifying || isFetchingPlaceDetails || !hasChanges();

  // ADD MORE / CUSTOM SERVICES — hamesha services list ke neeche.
  const addCustomServiceBlock = (
    <div className={styles.addCustomServiceSection}>
      <div
        className={styles.addCustomServiceHeader}
        onClick={() => setShowAddCustomService(!showAddCustomService)}
      >
        <label className={styles.customCheckbox} onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={showAddCustomService}
            onChange={(e) => setShowAddCustomService(e.target.checked)}
          />
          <span className={styles.checkmark}></span>
        </label>
        <span className={styles.checkLable} style={{ fontSize: "14px", fontWeight: 600 }}>
          Add More Services
        </span>
      </div>

      {showAddCustomService && (
        <div className={styles.addCustomServiceCard}>
          <label className={styles.customServiceLabel}>Service Name</label>
          <div className={styles.customServiceInputWrapper}>
            <input
              type="text"
              className={styles.customServiceInput}
              placeholder="Enter Your Service Name"
              value={customServiceInput}
              maxLength={50}
              onChange={(e) =>
                setCustomServiceInput(e.target.value.slice(0, 50))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddCustomService();
                }
              }}
            />
            <button
              type="button"
              className={styles.addServiceBtn}
              onClick={handleAddCustomService}
              title="Add Service"
            >
              <img src="/svg/addMore-icon.svg" alt="addMore-icon" />
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <EditHeader title="Edit Business Details" agentName={agentName} />
      <div className={styles.containerBox}>


        <div className={styles.stage}>
          <div className={styles.container}>
            {/* LOADING OVERLAY */}
            {isFetchingPlaceDetails && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                  borderRadius: "8px",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div
                    className="newloader"
                    style={{ margin: "0 auto 12px" }}
                  ></div>
                  <p style={{ color: "#666", fontSize: "14px" }}>
                    Loading business details...
                  </p>
                </div>
              </div>
            )}

            {/* BUSINESS CATEGORY */}
            <div className={styles.businessCategoryWrapper}>
              <div className={styles.BusinessInfo}>
                <label className={styles.LableBusiness}>Business Category</label>
                <div className={styles.searchBox}>
                  {businessTypeSearch === "" && (
                    <img src="/svg/Search-Icon.svg" alt="Search-Icon" />
                  )}
                  <input
                    maxLength={50}
                    placeholder="Search your business category"
                    value={businessTypeSearch}
                    onChange={(e) => {
                      setBusinessTypeSearch(e.target.value);
                      if (touched.businessType) {
                        const err = validateField("businessType", e.target.value);
                        setErrors((prev) => ({ ...prev, businessType: err }));
                      }
                    }}
                    onFocus={() => setShowBusinessTypeDropdown(true)}
                    onBlur={() =>
                      setTimeout(() => setShowBusinessTypeDropdown(false), 200)
                    }
                    disabled={isFetchingPlaceDetails}
                  />
                </div>
              </div>
              {touched.businessType && errors.businessType && (
                <p className={styles.errorText} style={{ position: "relative", padding: "4px 0 0" }}>
                  {errors.businessType}
                </p>
              )}

              {showBusinessTypeDropdown && (
                <div className={styles.list}>
                  {filteredBusinessTypes.map((item, i) => (
                    <div
                      key={i}
                      className={styles.listItem}
                      onMouseDown={() => handleBusinessTypeSelect(item)}
                    >
                      {item.display}
                    </div>
                  ))}

                  {isNewCategory && (
                    <div
                      className={`${styles.listItem} ${styles.newItem}`}
                      onMouseDown={handleBusinessTypeCustom}
                    >
                      {businessTypeSearch}
                      <span className={styles.newTag}>Add</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* CUSTOMER SERVICES / BUSINESS SERVICES SECTION (DIRECTLY UNDER BUSINESS CATEGORY) */}
            <div className={styles.servicesSectionMain}>
              <label className={styles.servicesSectionLabel}>
                Business Services *
              </label>

              {/* QUICK SEARCH FOR SERVICES — custom category me nahi dikhta */}
              {!isCustomCategory && (
                <div className={styles.serviceSearchBox}>
                  <span className={styles.serviceSearchIcon}>
                    <img src="/svg/Search-Icon.svg" alt="Search icon" />
                  </span>
                  <input
                    type="text"
                    placeholder="Quick find Business services"
                    className={styles.serviceSearchInput}
                    value={serviceSearchTerm}
                    onChange={(e) => setServiceSearchTerm(e.target.value)}
                  />
                </div>
              )}

              {/* LIST OF SERVICES WITH CHECKBOXES — custom category me jab tak
                  user ek bhi service add nahi karta, khaali list ka box nahi
                  dikhta. */}
              {!(isCustomCategory && allAvailableServices.length === 0) && (
              <div className={styles.servicesListWrapper}>
                <div className={styles.servicesOptionList} ref={servicesListRef}>
                  {filteredServices.length > 0 ? (
                    filteredServices.map((service) => {
                      const isChecked = formData.selectedServices?.includes(service);
                      return (
                        <div
                          className={`${styles.serviceOption} ${isChecked ? styles.serviceOptionSelected : ""
                            }`}
                          // Search/category badalne par list reorder hoti hai —
                          // index key se React galat row ka checkbox state reuse
                          // kar sakta hai. Naam hi stable identity hai.
                          key={service}
                          onClick={() => handleServiceToggle(service)}
                        >
                          <div className={styles.serviceForFlex}>
                            {/* <div className={styles.serviceIcon}>
                              <img
                                src={categoryIcon}
                                alt="service-icon"
                                className={styles.serviceIconImg}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "/images/other.png";
                                }}
                              />
                            </div> */}
                            <div className={styles.serviceTextDiv}>
                              <span className={styles.serviceName}>{service}</span>
                              {/* <p className={styles.serviceSubtype}>{categorySubtype}</p> */}
                            </div>
                          </div>

                          <div onClick={(e) => e.stopPropagation()}>
                            <label className={styles.customCheckbox}>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleServiceToggle(service)}
                              />
                              <span className={styles.checkmark}></span>
                            </label>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className={styles.noServiceFound}>No services found</p>
                  )}
                  <div style={{ height: 1 }} />
                </div>
              </div>
              )}

              {touched.selectedServices && errors.selectedServices && (
                <p className={styles.errorText} style={{ position: "relative", padding: 0 }}>
                  {errors.selectedServices}
                </p>
              )}

              {addCustomServiceBlock}
            </div>

            {/* BUSINESS INFO (GOOGLE SEARCH) */}
            <div className={styles.BusinessInfo}>
              <label className={styles.LableBusiness}>Business Info</label>
              <CustomAutoSearch
                value={formData.displayBusinessName || ""}
                placeholder="Business name as it appears on Google"
                results={googleResults}
                backgroundColor="#ffffff"
                padding="12px 0px"
                maxLength={50}
                onChange={(text) => {
                  setFormData((prev) => ({ ...prev, displayBusinessName: text }));
                  fetchGoogleResults(text);
                }}
                onSelect={(item) => handlePlaceSelect(item)}
                fetchResults={fetchGoogleResults}
              />
            </div>

            {/* DETAIL FORM */}
            <div className={styles.detailForm}>
              {/* WEBSITE URL */}
              <div className={styles.formGroupMain}>
                <div className={styles.formGroup}>
                  <label>Website (URL)</label>
                  <input
                    placeholder="https://yourbusiness.com"
                    value={formData.businessUrl || ""}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    onBlur={() =>
                      setTouched((prev) => ({ ...prev, businessUrl: true }))
                    }
                    disabled={isFetchingPlaceDetails}
                  />
                  {isVerifying && (
                    <div className={`newloader ${styles.successIcon}`}></div>
                  )}
                  {!isVerifying && formData.businessUrl && isUrlValid === true && (
                    <span className={styles.successIcon}>
                      <img src="/svg/check-icon.svg" alt="valid" />
                    </span>
                  )}
                  {!isVerifying && formData.businessUrl && isUrlValid === false && (
                    <span className={styles.successIcon}>
                      <img src="/svg/cross-icon.svg" alt="invalid" />
                    </span>
                  )}
                </div>
                {touched.businessUrl && errors.businessUrl && (
                  <p className={styles.errorText}>{errors.businessUrl}</p>
                )}
              </div>

              {/* NO WEBSITE CHECKBOX */}
              <div className={styles.chekboxDiv2}>
                <label className={styles.customCheckbox}>
                  <input
                    type="checkbox"
                    checked={formData.noBusinessWebsite || false}
                    onChange={(e) => handleNoWebsiteCheckbox(e.target.checked)}
                    disabled={isVerifying || isFetchingPlaceDetails}
                  />
                  <span className={styles.crossmark}></span>
                  <p className={styles.checkLable}>
                    I do not have Business website
                  </p>
                </label>
              </div>

              {/* BUSINESS NAME */}
              <div className={styles.formGroupMain}>
                <div className={styles.formGroup}>
                  <label>Business Name *</label>
                  <input
                    placeholder="Your Business Name"
                    value={
                      formData.placeDetails?.businessName ||
                      formData.displayBusinessName ||
                      ""
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      const noNumbersOrEmojis = value
                        .replace(/[0-9]/g, "")
                        .replace(
                          /([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF\uDC00-\uDFFF])/g,
                          ""
                        );

                      setFormData((prev) => ({
                        ...prev,
                        displayBusinessName: noNumbersOrEmojis,
                        placeDetails: {
                          ...prev.placeDetails,
                          businessName: noNumbersOrEmojis,
                        },
                      }));
                    }}
                    onBlur={() =>
                      setTouched((prev) => ({ ...prev, businessName: true }))
                    }
                    maxLength={50}
                    disabled={isFetchingPlaceDetails}
                  />
                </div>
                {touched.businessName && errors.businessName && (
                  <p className={styles.errorText}>{errors.businessName}</p>
                )}
              </div>

              {/* PHONE NUMBER */}
              <div className={styles.formGroupMain}>
                <div className={styles.formGroup}>
                  <label>Phone Number *</label>
                  <PhoneInput
                    // Sirf is page ke flag ka border hatane ke liye.
                    containerClass={styles.phoneNoFlagBorder}
                    // Country dropdown me search bar.
                    enableSearch
                    country={selectedCountry}
                    value={formData.placeDetails?.phone || ""}
                    onChange={(phone, countryData) =>
                      handlePhoneChange(phone, countryData)
                    }
                    onBlur={() =>
                      setTouched((prev) => ({ ...prev, phone: true }))
                    }
                    inputStyle={{
                      width: "100%",
                      height: "40px",
                      paddingLeft: "45px",
                      borderRadius: "5px",
                      border: "none",
                    }}
                    placeholder="+1 (123) 456-7890"
                    disabled={isFetchingPlaceDetails}
                  />
                </div>
                {touched.phone && errors.phone && (
                  <p className={styles.errorText}>{errors.phone}</p>
                )}
              </div>

              {/* ADDRESS */}
              <div className={styles.formGroupMain}>
                <div className={styles.formGroup}>
                  <label>Address *</label>
                  <GoogleAddressInputWithDropdown
                    ref={addressInputRef}
                    value={formData.placeDetails?.address || ""}
                    onChange={handleAddressChange}
                    placeholder="Your Business Location"
                    maxLength={300}
                    disabled={isFetchingPlaceDetails}
                  />
                </div>
                {touched.address && errors.address && (
                  <p className={styles.errorText}>{errors.address}</p>
                )}
              </div>

              {/* BUSINESS EMAIL */}
              <div className={styles.formGroup}>
                <label>Business Email</label>
                <input
                  type="email"
                  placeholder="Business Email Address"
                  value={formData.placeDetails?.email || ""}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      placeDetails: {
                        ...prev.placeDetails,
                        email: e.target.value,
                      },
                    }));
                  }}
                  disabled={isFetchingPlaceDetails}
                />
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER WITH SAVE BUTTON */}
        <div className={styles.footerWrapper}>
          <div className={styles.footer}>
            <div className={styles.TipDiv}>
              <img src="/svg/tip_icon.svg" alt="tip_icon" />
              <p className={styles.tipText}>
                <span className={styles.TipTitle}>Tip:</span> Changes will be saved
                immediately
              </p>
            </div>

            <div className={styles.FooterNav}>
              <div className={styles.steps}>{/* Empty space for alignment */}</div>

              <button
                className={styles.continueBtn}
                onClick={handleSave}
                disabled={isSaveDisabled}
                style={{
                  opacity: isSaveDisabled ? 0.5 : 1,
                  cursor: isSaveDisabled ? "not-allowed" : "pointer",
                }}
              >
                {isSaving ? "Saving..." : "Save"}
                {!isSaving && (
                  <img className={styles.arrow} src="/svg/arrow_2.svg" alt="" />
                )}
              </button>
            </div>
          </div>
        </div>
        {/* POPUP */}
        {showPopup && (
          <PopUp
            type={popupType}
            message={popupMessage}
            onClose={() => setShowPopup(false)}
          />
        )}
      </div>
    </>
  );
};

export default EditBusinessDetails;
